import { type SubscriptionProductClient } from "@fitness-app/data-models/entities/ProductClient";
import { areInstallmentsSettled } from "@fitness-app/utils/src/products/areInstallmentsSettled";

/**
 * Local pre-check for the OFFER actions — may this client be offered a payoff
 * (link, granted offer)? It only decides whether to SHOW them; the server
 * (resolvePayoffContext with `client_offer`, TRE-1514) stays authoritative and
 * re-validates the window, the ±48h billing guard and the offer on every call.
 *
 * Booking a transfer is a different question — see `isLocallySettleable`.
 */
export const isLocallyPayoffEligible = (productClient: SubscriptionProductClient): boolean => {
  const { accessPeriod } = productClient;

  return Boolean(
    !productClient.archivedInfo &&
    productClient.status === "active" &&
    productClient.paymentType === "recurring" &&
    accessPeriod &&
    (accessPeriod.installments ?? 0) > 1 &&
    accessPeriod.status === "active" &&
    // Manual installments qualify since TRE-1737 — the payoff is a single
    // transaction paid on the payoff page, so the missing saved card is not a
    // reason to hide the offer.
    // At least TWO installments must remain. With only the final one left there
    // is nothing to "pay off early" — the client pays it on schedule anyway, so
    // offering a link (and a discount) at that point is pointless.
    accessPeriod.cycleNumber < (accessPeriod.installments ?? 0) - 1,
  );
};

/**
 * Can a transfer still be BOOKED against this plan (TRE-2016)?
 *
 * Mirrors the structural half the server keeps for `staff_settlement`
 * (resolvePayoffContext, TRE-1745): the plan has to exist, be a multi-installment
 * plan and still owe something. Everything the offer check adds on top of that —
 * the last-installment rule, the ±48h billing window, an active subscription —
 * asks "does she qualify for the offer", which is not a question about money
 * that already landed on the bank account. The server turns each of those into a
 * warning the settlement modal shows before the operator books anything.
 *
 * Sharing the offer's check here hid the whole menu exactly when it was needed:
 * a two-installment plan with the first one paid has no offer left, so a client
 * paying the rest by transfer early left staff with no action at all.
 */
export const isLocallySettleable = (productClient: SubscriptionProductClient): boolean => {
  const { accessPeriod } = productClient;

  return Boolean(
    !productClient.archivedInfo &&
    productClient.status === "active" &&
    productClient.paymentType === "recurring" &&
    accessPeriod &&
    (accessPeriod.installments ?? 0) > 1 &&
    !areInstallmentsSettled(accessPeriod),
  );
};
